import type { ISoraVideoTool } from '~/interfaces/IMcpServer.js';
import { createVideoTask, pollTaskUntilComplete, SoraApiError } from '~/utils/soraApi.js';
import { downloadVideoFromUrl, VideoSaverError } from '~/utils/videoSaver.js';

/**
 * 文本生视频工具
 * 根据文本描述生成视频
 */
export const createVideoFromTextTool: ISoraVideoTool = {
  name: 'createVideoFromText',
  description: 'Generate videos from text descriptions using Sora AI. Supports different models, orientations, and durations.',
  inputSchema: {
    type: 'object',
    properties: {
      prompt: {
        type: 'string',
        description: 'Detailed description of the video you want to generate. Be specific about actions, scenes, and visual style.',
      },
      model: {
        type: 'string',
        enum: ['sora-2', 'sora-2-pro'],
        default: 'sora-2',
        description: 'Model to use. sora-2 supports 10/15s duration, sora-2-pro supports 15HD/25s duration.',
      },
      orientation: {
        type: 'string',
        enum: ['portrait', 'landscape'],
        default: 'landscape',
        description: 'Video orientation: portrait (9:16) or landscape (16:9).',
      },
      size: {
        type: 'string',
        enum: ['small', 'large'],
        default: 'small',
        description: 'Video resolution size.',
      },
      duration: {
        type: 'number',
        enum: [10, 15, 25],
        default: 15,
        description: 'Video duration in seconds. Note: sora-2 only supports 10/15s, sora-2-pro supports 15/25s.',
      },
    },
    required: ['prompt'],
  },

  async run(args: any) {
    console.error('========================================');
    console.error('[CreateVideoFromText] Tool execution started');
    console.error('========================================');

    try {
      // 提取并验证参数
      const {
        prompt,
        model = 'sora-2',
        orientation = 'landscape',
        size = 'small',
        duration = 15,
      } = args;

      // 参数验证
      if (!prompt || typeof prompt !== 'string') {
        return {
          content: [{
            type: 'text',
            text: 'Error: prompt is required and must be a string',
          }],
          isError: true,
        };
      }

      // 验证模型和时长的组合
      if (model === 'sora-2' && duration === 25) {
        return {
          content: [{
            type: 'text',
            text: 'Error: sora-2 model only supports duration of 10 or 15 seconds. Please use sora-2-pro for 25 second videos.',
          }],
          isError: true,
        };
      }

      console.error(`[CreateVideoFromText] Parameters:`);
      console.error(`  - Prompt: ${prompt.substring(0, 100)}${prompt.length > 100 ? '...' : ''}`);
      console.error(`  - Model: ${model}`);
      console.error(`  - Orientation: ${orientation}`);
      console.error(`  - Size: ${size}`);
      console.error(`  - Duration: ${duration}s`);

      // 步骤 1: 创建视频生成任务
      console.error('\n[CreateVideoFromText] Step 1/3: Creating video generation task...');
      const taskId = await createVideoTask({
        prompt,
        model: model as 'sora-2' | 'sora-2-pro',
        orientation: orientation as 'portrait' | 'landscape',
        size: size as 'small' | 'large',
        duration: duration as 10 | 15 | 25,
      });

      // 步骤 2: 轮询直到任务完成
      console.error(`\n[CreateVideoFromText] Step 2/3: Polling task ${taskId} until completion...`);
      const videoUrl = await pollTaskUntilComplete(taskId);

      // 步骤 3: 下载视频
      console.error('\n[CreateVideoFromText] Step 3/3: Downloading video...');
      const videoPath = await downloadVideoFromUrl(videoUrl);

      console.error('\n========================================');
      console.error('[CreateVideoFromText] SUCCESS!');
      console.error(`[CreateVideoFromText] Video saved at: ${videoPath}`);
      console.error('========================================\n');

      // 返回符合 MCP 规范的结果
      return {
        content: [{
          type: 'text',
          text: `Video generated successfully!\n\nPrompt: ${prompt}\nModel: ${model}\nDuration: ${duration}s\nOrientation: ${orientation}\n\nVideo saved at: ${videoPath}`,
        }],
      };

    } catch (error) {
      console.error('\n========================================');
      console.error('[CreateVideoFromText] ERROR occurred');
      console.error('========================================');

      if (error instanceof SoraApiError) {
        console.error(`[CreateVideoFromText] Sora API Error: ${error.message}`);
        return {
          content: [{
            type: 'text',
            text: `Sora API Error: ${error.message}`,
          }],
          isError: true,
        };
      }

      if (error instanceof VideoSaverError) {
        console.error(`[CreateVideoFromText] Video Save Error: ${error.message}`);
        return {
          content: [{
            type: 'text',
            text: `Failed to download video: ${error.message}`,
          }],
          isError: true,
        };
      }

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`[CreateVideoFromText] Unexpected error: ${errorMessage}`);

      return {
        content: [{
          type: 'text',
          text: `An unexpected error occurred: ${errorMessage}`,
        }],
        isError: true,
      };
    }
  },
};
